import { useCallback, useMemo, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { Navigate, Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { signInPath } from 'common/config/urls';
import {
  AUTH_PASSWORD_CHECKS,
  AUTH_PASSWORD_RULES,
  COUNTDOWN_INTERVAL,
  INITIAL_COUNTDOWN,
  SIGN_IN_VIEW,
} from 'common/constants/auth';
import { Alert, Button, Stack } from 'components/ui';
import { useCountdown } from 'components/ui/hooks';
import {
  AuthErrorPanel,
  AuthFormHeader,
  AuthPageStack,
  AuthPasswordField,
  type AuthPasswordRequirementItem,
  AuthPasswordRequirements,
  AuthSignInFooterPrompt,
  AuthSignInRedirectText,
  AuthSuccessPanel,
  AuthTitleText,
} from 'pages/Auth/components';
import {
  useAuthInitialFocus,
  useAuthSession,
  useClearFieldErrorOnValueChange,
  useRevalidateFieldOnDependencyChange,
  useSendPasswordChange,
} from 'pages/Auth/hooks';
import { buildSignInUrl, clearFieldErrorsByType } from 'pages/Auth/utils';
import {
  buildResetPasswordUrl,
  didAuthActionSucceed,
  getAuthActionErrorStatus,
  isMalformedPasswordResetLink,
} from './ResetPassword.utils';
import {
  SET_NEW_PASSWORD_CONFIRMATION_LABEL,
  SET_NEW_PASSWORD_CONFIRMATION_PLACEHOLDER,
  SET_NEW_PASSWORD_EXPIRED_LINK_ERROR,
  SET_NEW_PASSWORD_FORM_TITLE_LEAD,
  SET_NEW_PASSWORD_FORM_TITLE_TAIL,
  SET_NEW_PASSWORD_INVALID_LINK_ERROR,
  SET_NEW_PASSWORD_LABEL,
  SET_NEW_PASSWORD_LINK_ERROR_TITLE,
  SET_NEW_PASSWORD_PLACEHOLDER,
  SET_NEW_PASSWORD_REQUEST_NEW_LINK_LABEL,
  SET_NEW_PASSWORD_REQUIREMENTS_LABEL,
  SET_NEW_PASSWORD_SUBMIT_LABEL,
  SET_NEW_PASSWORD_TITLE_STANDARD,
  SET_NEW_PASSWORD_UPDATE_ERROR,
} from './SetNewPassword.constants';
import { SET_NEW_PASSWORD_SCHEMA } from './SetNewPassword.schema';
import { SET_NEW_PASSWORD_TEST_IDS } from './SetNewPassword.testIds';
import type { SetNewPasswordFooterLinkProps, SetNewPasswordFormValues } from './SetNewPassword.types';

const getPasswordChecklist = (password: string, passwordConfirmation: string): AuthPasswordRequirementItem[] =>
  AUTH_PASSWORD_CHECKS.map((label, index) => {
    const isMatchingCheck = index === AUTH_PASSWORD_CHECKS.length - 1;
    const isMet = isMatchingCheck
      ? Boolean(password) && password === passwordConfirmation
      : AUTH_PASSWORD_RULES[index].test(password);

    return {
      isMet,
      label,
    };
  });

const SetNewPasswordFooterLink = ({ disabled = false }: SetNewPasswordFooterLinkProps) => (
  <Stack alignItems="center">
    <AuthSignInFooterPrompt disabled={disabled} to={signInPath} />
  </Stack>
);

const SetNewPassword = () => {
  const navigate = useNavigate();
  const { token, uid } = useParams<{ token: string; uid: string }>();
  const sendPasswordChange = useSendPasswordChange();
  const [linkError, setLinkError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { hasSession } = useAuthSession();
  const handleSuccessRedirect = useCallback(() => {
    navigate(signInPath, { replace: true });
  }, [navigate]);
  const countdown = useCountdown({
    enabled: isSuccess && !hasSession,
    initialCountdown: INITIAL_COUNTDOWN,
    intervalMs: COUNTDOWN_INTERVAL,
    onComplete: handleSuccessRedirect,
  });

  const form = useForm<SetNewPasswordFormValues>({
    defaultValues: {
      password: '',
      passwordConfirmation: '',
    },
    mode: 'onTouched',
    reValidateMode: 'onChange',
    resolver: yupResolver(SET_NEW_PASSWORD_SCHEMA),
  });

  const clearPasswordServerErrors = useCallback(() => {
    clearFieldErrorsByType(form, ['password', 'passwordConfirmation'], ['server']);
  }, [form]);

  const dismissSubmitError = useCallback(() => {
    setSubmitError(null);
    clearPasswordServerErrors();
  }, [clearPasswordServerErrors]);

  const focusPasswordField = useCallback(() => {
    form.setFocus('password');
  }, [form]);

  const watchedPassword = form.watch('password');
  const watchedPasswordConfirmation = form.watch('passwordConfirmation');
  useRevalidateFieldOnDependencyChange({
    dependencies: [watchedPassword],
    fieldName: 'passwordConfirmation',
    form,
  });
  useClearFieldErrorOnValueChange({
    errorTypes: ['server'],
    fieldNames: ['password', 'passwordConfirmation'],
    form,
    onClear: dismissSubmitError,
    shouldClear: () => Boolean(submitError),
    values: [watchedPassword, watchedPasswordConfirmation],
  });

  const passwordChecklist = useMemo(
    () => getPasswordChecklist(watchedPassword ?? '', watchedPasswordConfirmation ?? ''),
    [watchedPassword, watchedPasswordConfirmation]
  );
  const isPasswordChecklistValid = passwordChecklist.every(check => check.isMet);
  const hasLoggedInUser = useMemo(() => hasSession && isSuccess, [hasSession, isSuccess]);
  const hasMalformedLink = isMalformedPasswordResetLink(uid, token);
  const effectiveLinkError = hasMalformedLink ? SET_NEW_PASSWORD_INVALID_LINK_ERROR : linkError;

  useAuthInitialFocus(focusPasswordField, !isSuccess && !isSubmitting && !effectiveLinkError);

  if (hasLoggedInUser) {
    return <Navigate replace to={buildSignInUrl({ view: SIGN_IN_VIEW.WORKSPACE_SELECTION })} />;
  }

  const handleSubmit = form.handleSubmit(async values => {
    if (isSubmitting) {
      return;
    }

    if (hasMalformedLink) {
      setLinkError(SET_NEW_PASSWORD_INVALID_LINK_ERROR);
      return;
    }

    const resolvedUid = uid as string;
    const resolvedToken = token as string;

    setLinkError(null);
    clearPasswordServerErrors();
    setSubmitError(null);
    setIsSubmitting(true);

    const response = await sendPasswordChange(
      values.password,
      values.passwordConfirmation,
      resolvedUid,
      resolvedToken
    );

    if (didAuthActionSucceed(response)) {
      setIsSuccess(true);
      setIsSubmitting(false);
      return;
    }

    // Token/uid failures are treated as link problems so the UI can steer the user toward requesting a fresh reset email.
    const errorStatus = getAuthActionErrorStatus(response);

    if (errorStatus === 400 || errorStatus === 404) {
      setLinkError(SET_NEW_PASSWORD_EXPIRED_LINK_ERROR);
    } else {
      form.setError('password', { message: SET_NEW_PASSWORD_UPDATE_ERROR, type: 'server' });
      form.setError('passwordConfirmation', { message: SET_NEW_PASSWORD_UPDATE_ERROR, type: 'server' });
      setSubmitError(SET_NEW_PASSWORD_UPDATE_ERROR);
    }

    setIsSubmitting(false);
  });

  let liveContent;

  if (isSuccess) {
    liveContent = (
      <AuthSuccessPanel
        description={<AuthSignInRedirectText countdown={countdown} />}
        extraAction={<AuthSignInFooterPrompt replace to={signInPath} />}
        title={SET_NEW_PASSWORD_TITLE_STANDARD}
      />
    );
  } else if (effectiveLinkError) {
    liveContent = (
      <AuthErrorPanel
        description={effectiveLinkError}
        extraAction={<AuthSignInFooterPrompt replace to={signInPath} />}
        mainAction={(
          <Button<typeof RouterLink>
            component={RouterLink}
            replace
            to={buildResetPasswordUrl()}
            variant="outlined"
            visualVariant="primaryOutline"
          >
            {SET_NEW_PASSWORD_REQUEST_NEW_LINK_LABEL}
          </Button>
        )}
        title={SET_NEW_PASSWORD_LINK_ERROR_TITLE}
      />
    );
  } else {
    liveContent = (
      <>
        <AuthPasswordRequirements items={passwordChecklist} title={SET_NEW_PASSWORD_REQUIREMENTS_LABEL} />
        <Stack component="form" noValidate onSubmit={handleSubmit} spacing={2.25}>
          <AuthPasswordField
            autoComplete="new-password"
            disabled={isSubmitting}
            error={Boolean(form.formState.errors.password)}
            id="auth-set-password-input"
            label={SET_NEW_PASSWORD_LABEL}
            placeholder={SET_NEW_PASSWORD_PLACEHOLDER}
            {...form.register('password')}
          />
          <AuthPasswordField
            autoComplete="new-password"
            disabled={isSubmitting}
            error={Boolean(form.formState.errors.passwordConfirmation)}
            id="auth-set-password-confirmation"
            label={SET_NEW_PASSWORD_CONFIRMATION_LABEL}
            placeholder={SET_NEW_PASSWORD_CONFIRMATION_PLACEHOLDER}
            {...form.register('passwordConfirmation')}
          />
          <Button
            data-testid={SET_NEW_PASSWORD_TEST_IDS.submitButton}
            disabled={!isPasswordChecklistValid}
            loading={isSubmitting}
            type="submit"
            variant="contained"
            visualVariant="positive"
          >
            {SET_NEW_PASSWORD_SUBMIT_LABEL}
          </Button>
        </Stack>
      </>
    );
  }

  return (
    <AuthPageStack>
      <AuthFormHeader
        title={<AuthTitleText lead={SET_NEW_PASSWORD_FORM_TITLE_LEAD} tail={SET_NEW_PASSWORD_FORM_TITLE_TAIL} />}
      />
      {submitError ? (
        <Alert onClose={dismissSubmitError} severity="error" visualVariant="inlineError">
          {submitError}
        </Alert>
      ) : null}
      {liveContent}

      {!isSuccess && !effectiveLinkError ? <SetNewPasswordFooterLink disabled={isSubmitting} /> : null}
    </AuthPageStack>
  );
};

export default SetNewPassword;
